import { FC, ReactNode } from "react";

import { Tab, TabList, Tabs } from "@hightouchio/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate } from "react-router-dom";

import { Page } from "src/components/layout";
import { useUser } from "src/contexts/user-context";
import { ResourcePermissionGrant } from "src/graphql";

type Props = {
  children: ReactNode;
  route: string;
  title: string;
};

const UNAUTHORIZED_TABS = [
  {
    label: "Members",
    route: "members",
  },
];

const AUTHORIZED_TABS = [
  {
    label: "Workspace",
    route: "workspace",
  },
  {
    label: "Members",
    route: "members",
  },
  { label: "Roles", route: "roles" },
  {
    label: "Cloud providers",
    route: "cloud-providers",
  },
  { label: "API keys", route: "api-keys" },
  { label: "Storage", route: "storage" },
  { label: "Tunnels", route: "tunnels" },
  {
    label: "SSO",
    route: "sso",
  },
  {
    label: "Audit log",
    route: "audit-log",
  },
];

export const Settings: FC<Readonly<Props>> = ({ children, route, title }) => {
  const { featureFlags, hasPermissions } = useUser();
  const { appShowUsageTab, appAllowCheckout } = useFlags();

  const navigate = useNavigate();

  const authorized = hasPermissions([{ resource: "workspace", grants: [ResourcePermissionGrant.Read] }]);

  const authorizedTabs = [...AUTHORIZED_TABS];

  if (authorized) {
    if (appAllowCheckout) {
      authorizedTabs.splice(3, 0, { label: "Billing", route: "billing" });
    } else if (appShowUsageTab) {
      authorizedTabs.splice(3, 0, { label: "Usage", route: "usage" });
    }
    if (featureFlags?.git_sync) {
      authorizedTabs.push({ label: "Version control", route: "version-control" });
    }
  }

  const TABS = authorized ? authorizedTabs : UNAUTHORIZED_TABS;

  return (
    <Page title={`${title} - Settings`}>
      <Tabs
        onChange={(index) => {
          const tab = TABS[index];

          if (tab) {
            navigate(`/settings/${tab.route}`);
          }
        }}
        index={TABS.map((tab) => tab.route).indexOf(route)}
        mb={8}
      >
        <TabList>
          {TABS.map((t) => (
            <Tab key={t.route}>{t.label}</Tab>
          ))}
        </TabList>
      </Tabs>
      {children}
    </Page>
  );
};
